import PropTypes from 'prop-types';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { adminMobileNavItems } from 'src/layouts/main/config-navigation';

// Components
import AdminHeader from 'src/layouts/admin/AdminHeader';
import NavList from 'src/layouts/main/nav-mobile/nav-list';
import Logo from 'src/components/logo';
import Scrollbar from 'src/components/scrollbar';
import WalletConnectButton from 'src/components/wallet-connect/WalletConnectButton';
import useAdminSocket from 'src/hooks/useAdminSocket';

const DRAWER_WIDTH = 260;

export default function AdminLayout({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Admin socket
  useAdminSocket();
  // Mobile nav handler
  const toggleDrawer = () => setDrawerOpen((prev) => !prev);

  // Desktop Navigation (if needed for future expansion)
  // For now, mobile only, but this slot is ready for future desktop nav/sidebar
  const renderNav = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Top */}
      <Box>
        <Logo small={false} sx={{ m: 2 }} />
        <Divider />
      </Box>

      {/* Middle (scrollable nav) */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List component="nav" disablePadding onClick={toggleDrawer}>
          {adminMobileNavItems.map((link) => (
            <NavList key={link.title} item={link} />
          ))}
        </List>
      </Box>

      {/* Bottom (fixed) */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <WalletConnectButton />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Mobile Drawer Nav */}
      {!isDesktop && (
        <Drawer
          open={drawerOpen}
          onClose={toggleDrawer}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper',
              zIndex: 1202,
            },
          }}
        >
          <Scrollbar sx={{ height: '100%' }}>{renderNav}</Scrollbar>
        </Drawer>
      )}

      <AdminHeader toggleDrawer={toggleDrawer} />

      {/* Layout container for children/content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, md: 4 },
          minHeight: '80vh',
          width: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

AdminLayout.propTypes = {
  children: PropTypes.node,
};
